import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, FileText, GraduationCap, ShieldCheck, Lock, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { trainingAuth } from '../services/trainingApi';

const Login = () => {
  const location = useLocation();
  const initialMode = new URLSearchParams(location.search).get('portal') === 'szkolenia' ? 'training' : 'crm';
  const [mode, setMode] = useState(initialMode); // 'crm' | 'training'

  // --- Wspólny stan podglądu hasła ---
  const [showPassword, setShowPassword] = useState(false);

  // --- Logowanie CRM (faktury) ---
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  // --- Logowanie do szkoleń (AD) ---
  const [adUsername, setAdUsername] = useState('');
  const [adPassword, setAdPassword] = useState('');
  const [trainingLoading, setTrainingLoading] = useState(false);

  const credentialsRef = useRef({ email: '', password: '' });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const savedEmail = sessionStorage.getItem('login_email');
    const savedPassword = sessionStorage.getItem('login_password');
    const savedRequires2FA = sessionStorage.getItem('login_requires2fa') === 'true';
    if (savedEmail) {
      setEmail(savedEmail);
      credentialsRef.current.email = savedEmail;
    }
    if (savedPassword) {
      setPassword(savedPassword);
      credentialsRef.current.password = savedPassword;
    }
    if (savedRequires2FA) {
      setRequires2FA(true);
    }
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (requires2FA && !email) {
      const savedEmail = sessionStorage.getItem('login_email');
      const savedPassword = sessionStorage.getItem('login_password');
      if (savedEmail || credentialsRef.current.email) {
        const emailToRestore = savedEmail || credentialsRef.current.email;
        const passwordToRestore = savedPassword || credentialsRef.current.password;
        setEmail(emailToRestore);
        setPassword(passwordToRestore);
        credentialsRef.current = { email: emailToRestore, password: passwordToRestore };
      }
    }
    if (!requires2FA) {
      const savedRequires2FA = sessionStorage.getItem('login_requires2fa') === 'true';
      if (savedRequires2FA) {
        setRequires2FA(true);
      }
    }
  }, [requires2FA, email, twoFactorCode]);

  const validateEmail = (value) => {
    if (!value) return 'Email jest wymagany';
    if (!/^\S+@\S+$/i.test(value)) return 'Nieprawidłowy format email';
    return '';
  };

  const validatePassword = (value) => {
    if (!value) return 'Hasło jest wymagane';
    if (value.length < 6) return 'Hasło musi mieć co najmniej 6 znaków';
    return '';
  };

  const handleCrmSubmit = async (e) => {
    e.preventDefault();

    if (requires2FA) {
      if (!twoFactorCode || twoFactorCode.length !== 6) {
        toast.error('Wprowadź 6-cyfrowy kod weryfikacyjny');
        return;
      }
      const result = await login(email, password, twoFactorCode);
      if (result.success) {
        try {
          sessionStorage.removeItem('login_email');
          sessionStorage.removeItem('login_password');
          sessionStorage.removeItem('login_requires2fa');
        } catch (err) {
          console.error('Failed to clear sessionStorage:', err);
        }
        setRequires2FA(false);
        setTwoFactorCode('');
        setEmail('');
        setPassword('');
        credentialsRef.current = { email: '', password: '' };
        toast.success('Zalogowano pomyślnie');
        navigate(result.requiresMfaSetup ? '/app/mfa-settings' : '/');
      } else {
        toast.error(result.error || 'Nieprawidłowy kod z aplikacji Authenticator');
        setTwoFactorCode('');
      }
      return;
    }

    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    if (emailErr || passwordErr) {
      setEmailError(emailErr);
      setPasswordError(passwordErr);
      return;
    }
    setEmailError('');
    setPasswordError('');

    const result = await login(email, password, null);
    if (result.success) {
      setEmail('');
      setPassword('');
      toast.success('Zalogowano pomyślnie');
      navigate(result.requiresMfaSetup ? '/app/mfa-settings' : '/');
    } else if (result.requiresTwoFactor) {
      credentialsRef.current = { email, password };
      try {
        sessionStorage.setItem('login_email', email);
        sessionStorage.setItem('login_password', password);
        sessionStorage.setItem('login_requires2fa', 'true');
      } catch (err) {
        console.error('Failed to save to sessionStorage:', err);
      }
      setRequires2FA(true);
      requestAnimationFrame(() => {
        if (isMountedRef.current) {
          setRequires2FA(true);
          if (!email) setEmail(credentialsRef.current.email);
          if (!password) setPassword(credentialsRef.current.password);
        }
      });
      toast.success(result.message || 'Wprowadź kod z aplikacji Authenticator');
    } else {
      toast.error(result.error || 'Błąd logowania');
      if (result.accountLocked) {
        setRequires2FA(false);
        setTwoFactorCode('');
      }
    }
  };

  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    if (!adUsername.trim() || !adPassword) {
      toast.error('Podaj login i hasło domenowe');
      return;
    }
    try {
      setTrainingLoading(true);
      await trainingAuth.login(adUsername.trim(), adPassword);
      toast.success('Zalogowano do portalu szkoleń');
      setAdPassword('');
      navigate('/szkolenia');
    } catch (error) {
      const msg = error.response?.data?.error || 'Nie udało się zalogować. Sprawdź dane domenowe.';
      toast.error(msg);
    } finally {
      setTrainingLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setShowPassword(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Karta logowania */}
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 overflow-hidden">
          {/* Nagłówek z logo */}
          <div className="px-8 pt-8 pb-4 text-center">
            <div className="mx-auto h-20 w-20 flex items-center justify-center mb-3">
              <img
                src="/images/cropped-logo-szpital_akredytacja.png"
                alt="Szpital Łapy"
                className="h-20 w-20 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">CRM LAPY</h1>
            <p className="mt-1 text-sm text-gray-500">
              {mode === 'crm' ? 'Logowanie do systemu faktur i CRM' : 'Portal szkoleń i ankiet pracowniczych'}
            </p>
          </div>

          {/* Przełącznik trybu */}
          <div className="px-8">
            <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl">
              <button
                type="button"
                onClick={() => switchMode('crm')}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'crm'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="h-4 w-4" />
                Faktury / CRM
              </button>
              <button
                type="button"
                onClick={() => switchMode('training')}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'training'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <GraduationCap className="h-4 w-4" />
                Szkolenia / Ankiety
              </button>
            </div>
          </div>

          {/* Formularze */}
          <div className="px-8 py-6">
            {mode === 'crm' ? (
              <form className="space-y-4" onSubmit={handleCrmSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Adres email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    disabled={requires2FA}
                    className={`block w-full px-3 py-2.5 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      emailError ? 'border-red-300' : 'border-gray-300'
                    } ${requires2FA ? 'bg-gray-100' : 'bg-white'}`}
                    placeholder="jan.kowalski@szpitallapy.pl"
                  />
                  {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Hasło
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError('');
                      }}
                      disabled={requires2FA}
                      className={`block w-full px-3 py-2.5 pr-10 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        passwordError ? 'border-red-300' : 'border-gray-300'
                      } ${requires2FA ? 'bg-gray-100' : 'bg-white'}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={requires2FA}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordError && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}
                </div>

                {requires2FA && (
                  <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <label htmlFor="twoFactorCode" className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <ShieldCheck className="h-4 w-4 text-blue-600" />
                      Kod z aplikacji Authenticator (6 cyfr)
                    </label>
                    <input
                      type="text"
                      id="twoFactorCode"
                      name="twoFactorCode"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoComplete="one-time-code"
                      className="block w-full px-4 py-3 border-2 border-blue-400 rounded-lg text-gray-900 text-center text-2xl tracking-[0.3em] font-mono font-bold bg-white focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500"
                      placeholder="000000"
                      maxLength="6"
                      autoFocus
                    />
                    <p className="mt-2 text-xs text-gray-600 text-center">
                      Otwórz aplikację Authenticator (Google, Microsoft, Authy) i wprowadź bieżący kod dla konta CRM
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Zaloguj się'}
                </button>
              </form>
            ) : (
              <form className="space-y-4" onSubmit={handleTrainingSubmit}>
                <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-indigo-800">
                    Zaloguj się <strong>kontem domenowym</strong> (Active Directory), aby obejrzeć szkolenia
                    obowiązkowe i wypełnić ankiety pracownicze.
                  </p>
                </div>

                <div>
                  <label htmlFor="adUsername" className="block text-sm font-medium text-gray-700 mb-1">
                    Login domenowy
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="adUsername"
                      name="adUsername"
                      type="text"
                      autoComplete="username"
                      value={adUsername}
                      onChange={(e) => setAdUsername(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="j.kowalski"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="adPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Hasło domenowe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="adPassword"
                      name="adPassword"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={adPassword}
                      onChange={(e) => setAdPassword(e.target.value)}
                      className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={trainingLoading}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {trainingLoading ? <LoadingSpinner size="sm" /> : 'Przejdź do szkoleń / ankiet'}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Szpital Powiatowy w Łapach · System CRM
        </p>
      </div>
    </div>
  );
};

export default Login;
